import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import type { Interview } from '@packages/commons';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { CommandList } from '../components/CommandList';
import {
  useRegisterCommands,
  useRegisterPage,
  useRegisterShortcut,
  usePluginNavigation,
} from '../hooks';
import type { Command as CommandType } from '../types';

const PLUGIN_ID = 'interview-plugin';
const INTERVIEWS_PAGE = 'interviews';
const INTERVIEWS_PLACEHOLDER = 'Search interviews...';

// ---------------------------------------------------------------------------
// useReleaseInterviews — returns the interview list from the current release.
// ---------------------------------------------------------------------------

const useReleaseInterviews = (): Interview[] => {
  const release = useFullRelease();
  return useMemo(
    () => (release?.interviews ?? []).filter((i: Interview) => i.id && i.name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [release?.interviews],
  );
};

// ---------------------------------------------------------------------------
// InterviewsPage — rendered when the palette navigates to INTERVIEWS_PAGE
// ---------------------------------------------------------------------------

const InterviewsPage: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const interviews = useReleaseInterviews();

  const handleSelect = (interviewId: string) => {
    // Persist the selection so the InterviewDesigner picks it up on mount.
    localStorage.setItem('rememberedInterview', JSON.stringify(interviewId));
    dispatch(push('/build/design'));
    close();
  };

  const commands = useMemo((): CommandType[] =>
    interviews.map((interview: Interview) => ({
      id: `interview-${interview.id}`,
      label: interview.name,
      description: interview.status === 'published' ? 'Published' : 'Draft',
      category: 'Interviews',
      keywords: [interview.name, interview.goal, interview.status],
      primary: {
        id: `interview-${interview.id}-open`,
        label: 'Open Interview',
        action: () => handleSelect(interview.id),
      },
    })),
  [interviews, handleSelect]);

  return <CommandList commands={commands} emptyMessage="No interviews found." />;
};

// ---------------------------------------------------------------------------
// InterviewPlugin — registers the global command and keyboard shortcut
// ---------------------------------------------------------------------------

/**
 * InterviewPlugin - Provides interview search and navigation
 *
 * Global commands:
 *   - "Go to Interview" → opens the interviews page
 *
 * Selecting an interview navigates directly to the Interview Designer
 * with that interview pre-selected.
 */
export const InterviewPlugin: React.FC = () => {
  const { navigateTo, openAndNavigate } = usePluginNavigation(PLUGIN_ID);

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'open-interview-search',
      label: 'Go to Interview',
      description: 'Search and navigate to an interview',
      category: 'Navigation',
      shortcut: 'mod+alt+i',
      keywords: ['interview', 'navigate', 'search', 'designer', 'open'],
      primary: {
        id: 'open-interview-search-action',
        label: 'Search Interviews',
        action: () => navigateTo(INTERVIEWS_PAGE, INTERVIEWS_PLACEHOLDER),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: INTERVIEWS_PAGE, component: InterviewsPage });

  useRegisterShortcut({
    shortcut: 'mod+alt+i',
    label: 'Go to Interview',
    handler: () => openAndNavigate(INTERVIEWS_PAGE, INTERVIEWS_PLACEHOLDER),
  });

  return null;
};
